<template>
  <div>
    <!-- Hero Header Section (ซ่อนเมื่อใช้ในหน้าต่างของหน้าอื่น — compact) -->
    <v-card v-if="!compact" class="hero-header mb-6" elevation="0">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="48" color="white">mdi-medal-outline</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">เครื่องราชอิสริยาภรณ์</h1>
          <p class="hero-subtitle">ประวัติการได้รับเครื่องราชอิสริยาภรณ์</p>
        </div>
        <v-spacer></v-spacer>
        <v-btn class="add-btn" large depressed @click="personnel_decorationAdd()">
          <v-icon left>mdi-plus</v-icon>
          เพิ่มเครื่องราชอิสริยาภรณ์
        </v-btn>
      </div>
    </v-card>

    <!-- Statistics Cards -->
    <v-row v-if="!compact" class="stats-row mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-1" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-medal</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ personnel_decorations.length }}</span>
            <span class="stat-label">รายการทั้งหมด</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-2" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-calendar-star</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ latestYear || "-" }}</span>
            <span class="stat-label">ปีล่าสุดที่ได้รับ (พ.ศ.)</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-3" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-tag-multiple</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ distinctClassCount }}</span>
            <span class="stat-label">ชั้นตราที่ได้รับ</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-4" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-file-check</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ withFileCount }}</span>
            <span class="stat-label">มีไฟล์แนบ</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Data Table Card -->
    <v-card class="main-table-card" elevation="0">
      <v-card-title class="table-header">
        <div class="header-left">
          <v-icon class="mr-3" color="#d9777b">mdi-format-list-bulleted</v-icon>
          <span class="header-title">รายการเครื่องราชอิสริยาภรณ์</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn v-if="compact" depressed color="primary" class="mr-3" @click="personnel_decorationAdd()">
          + เพิ่มประวัติ
        </v-btn>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="ค้นหา..."
          single-line
          hide-details
          dense
          outlined
          class="search-field"
          style="max-width: 350px"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="personnel_decorations"
        :search="search"
        :items-per-page="10"
        class="modern-table"
        loading-text="กำลังโหลดข้อมูล..."
        no-data-text="ไม่พบข้อมูลเครื่องราชอิสริยาภรณ์"
        no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
      >
        <template v-slot:item="{ item, index }">
          <tr class="table-row">
            <td class="text-center">
              <v-avatar size="32" color="#d9777b" class="white--text font-weight-bold">
                {{ index + 1 }}
              </v-avatar>
            </td>
            <td>
              <div class="branch-cell">
                <v-icon small color="#d9777b" class="mr-2">mdi-medal-outline</v-icon>
                {{ item.decoration_name }}
              </div>
            </td>
            <td>
              <v-chip small color="#fbecec" text-color="#b3151b" class="font-weight-medium">
                {{ item.decoration_class }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip small outlined color="#b3151b">
                <v-icon left small>mdi-calendar</v-icon>
                {{ formatDateThai(item.received_date) }}
              </v-chip>
            </td>
            <td>
              <span class="sub-branch-text">
                <template v-if="item.gazette_book || item.gazette_issue || item.gazette_page">
                  เล่ม {{ item.gazette_book || "-" }} ตอน {{ item.gazette_issue || "-" }} หน้า {{ item.gazette_page || "-" }}
                </template>
                <template v-else>-</template>
              </span>
            </td>
            <td class="text-center">
              <v-icon v-if="item.decoration_file" color="success" small>mdi-file-check-outline</v-icon>
              <v-icon v-else color="grey lighten-1" small>mdi-file-hidden</v-icon>
            </td>
            <td class="text-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="action-btn edit-btn mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="personnel_decorationEdit(item.id_pd)"
                  >
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>แก้ไข</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="action-btn delete-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="personnel_decorationDelete(item.id_pd)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>ลบ</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Dialog -->
    <v-dialog v-model="addpersonnel_decorationdialog" persistent max-width="800px" content-class="modern-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-header add-header">
          <v-icon class="mr-3" color="white" size="28">mdi-plus-circle-outline</v-icon>
          <span>เพิ่มเครื่องราชอิสริยาภรณ์</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addpersonnel_decorationdialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="addpersonnel_decorationform" lazy-validation>
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#d9777b" class="mr-2">mdi-medal-outline</v-icon>
                ข้อมูลเครื่องราชอิสริยาภรณ์
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-combobox
                    v-model="addpersonnel_decoration.decoration_class"
                    :items="classItems"
                    item-text="text"
                    item-value="abbr"
                    :return-object="false"
                    label="ชั้นตรา"
                    placeholder="เลือกชั้นตรา เช่น ตริตาภรณ์ช้างเผือก (ต.ช.)"
                    :rules="[v => !!v || 'กรุณาระบุชั้นตรา']"
                    hint="เลือกจากรายการเพื่อให้ระบบตรวจคุณสมบัติการขอครั้งถัดไปได้"
                    outlined
                    dense
                    prepend-inner-icon="mdi-tag-outline"
                    class="modern-input"
                    @change="v => fillOrderName(addpersonnel_decoration, v)"
                  ></v-combobox>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="addpersonnel_decoration.decoration_name"
                    label="ชื่อเครื่องราชอิสริยาภรณ์"
                    placeholder="เช่น เครื่องราชอิสริยาภรณ์อันเป็นที่เชิดชูยิ่งช้างเผือก"
                    :rules="[v => !!v || 'กรุณาระบุชื่อเครื่องราชอิสริยาภรณ์']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-medal-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="addReceivedDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatDateThai(addpersonnel_decoration.received_date)"
                        label="วันที่ได้รับพระราชทาน"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="addpersonnel_decoration.received_date = null"
                        :rules="[v => !!v || 'กรุณาระบุวันที่ได้รับพระราชทาน']"
                        class="modern-input"
                      />
                    </template>
                    <v-date-picker
                      v-model="addpersonnel_decoration.received_date"
                      locale="th-TH"
                      @input="addReceivedDateMenu = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="addGazetteDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatDateThai(addpersonnel_decoration.gazette_date)"
                        label="วันที่ประกาศราชกิจจานุเบกษา"
                        prepend-inner-icon="mdi-calendar-clock"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="addpersonnel_decoration.gazette_date = null"
                        class="modern-input"
                      />
                    </template>
                    <v-date-picker
                      v-model="addpersonnel_decoration.gazette_date"
                      locale="th-TH"
                      @input="addGazetteDateMenu = false"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <div class="section-title">
                <v-icon color="#2e7d32" class="mr-2">mdi-newspaper-variant-outline</v-icon>
                ราชกิจจานุเบกษา
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="addpersonnel_decoration.gazette_book"
                    label="เล่มที่"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="addpersonnel_decoration.gazette_issue"
                    label="ตอนที่"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="addpersonnel_decoration.gazette_page"
                    label="หน้า"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="addpersonnel_decoration.decoration_note"
                    label="หมายเหตุ (ถ้ามี)"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <div
                    v-if="addpersonnel_decoration.decoration_file"
                    class="d-flex align-center pa-3 rounded-lg"
                    style="background-color: #e8f5e9; border: 1px solid #81c784;"
                  >
                    <v-chip small color="success" class="mr-3">
                      <v-icon small left>mdi-check</v-icon>มีไฟล์แนบแล้ว
                    </v-chip>
                    <v-btn icon small color="primary" class="mr-2" :href="'/HRvecDecoration/' + addpersonnel_decoration.decoration_file" target="_blank">
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                    <v-btn icon small color="error" @click="addpersonnel_decoration.decoration_file = ''">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <span class="ml-2 text-caption grey--text">ลบไฟล์เดิมเพื่ออัปโหลดใหม่</span>
                  </div>
                  <v-file-input
                    v-else
                    v-model="add_decoration_file_tem"
                    outlined
                    dense
                    accept=".pdf,.jpg,.jpeg,.png"
                    label="แนบไฟล์ประกาศ/เกียรติบัตร (.pdf/.jpg/.png)"
                    prepend-icon=""
                    prepend-inner-icon="mdi-paperclip"
                    show-size
                    class="modern-input"
                  />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="addpersonnel_decorationdialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn large depressed color="#d9777b" dark @click="addpersonnel_decorationSubmit()" class="save-btn" :loading="submitting">
            <v-icon left>mdi-content-save-outline</v-icon>
            บันทึกข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editpersonnel_decorationdialog" persistent max-width="800px" content-class="modern-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-header edit-header">
          <v-icon class="mr-3" color="white" size="28">mdi-pencil-outline</v-icon>
          <span>แก้ไขเครื่องราชอิสริยาภรณ์</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editpersonnel_decorationdialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="editpersonnel_decorationform" lazy-validation>
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#a86400" class="mr-2">mdi-medal-outline</v-icon>
                ข้อมูลเครื่องราชอิสริยาภรณ์
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-combobox
                    v-model="editpersonnel_decoration.decoration_class"
                    :items="classItems"
                    item-text="text"
                    item-value="abbr"
                    :return-object="false"
                    label="ชั้นตรา"
                    :rules="[v => !!v || 'กรุณาระบุชั้นตรา']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-tag-outline"
                    class="modern-input"
                    @change="v => fillOrderName(editpersonnel_decoration, v)"
                  ></v-combobox>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editpersonnel_decoration.decoration_name"
                    label="ชื่อเครื่องราชอิสริยาภรณ์"
                    :rules="[v => !!v || 'กรุณาระบุชื่อเครื่องราชอิสริยาภรณ์']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-medal-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="editReceivedDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatDateThai(editpersonnel_decoration.received_date)"
                        label="วันที่ได้รับพระราชทาน"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="editpersonnel_decoration.received_date = null"
                        :rules="[v => !!v || 'กรุณาระบุวันที่ได้รับพระราชทาน']"
                        class="modern-input"
                      />
                    </template>
                    <v-date-picker
                      v-model="editpersonnel_decoration.received_date"
                      locale="th-TH"
                      @input="editReceivedDateMenu = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="editGazetteDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatDateThai(editpersonnel_decoration.gazette_date)"
                        label="วันที่ประกาศราชกิจจานุเบกษา"
                        prepend-inner-icon="mdi-calendar-clock"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="editpersonnel_decoration.gazette_date = null"
                        class="modern-input"
                      />
                    </template>
                    <v-date-picker
                      v-model="editpersonnel_decoration.gazette_date"
                      locale="th-TH"
                      @input="editGazetteDateMenu = false"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <div class="section-title">
                <v-icon color="#e53935" class="mr-2">mdi-newspaper-variant-outline</v-icon>
                ราชกิจจานุเบกษา
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editpersonnel_decoration.gazette_book"
                    label="เล่มที่"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editpersonnel_decoration.gazette_issue"
                    label="ตอนที่"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editpersonnel_decoration.gazette_page"
                    label="หน้า"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="editpersonnel_decoration.decoration_note"
                    label="หมายเหตุ (ถ้ามี)"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <div
                    v-if="editpersonnel_decoration.decoration_file"
                    class="d-flex align-center pa-3 rounded-lg"
                    style="background-color: #e8f5e9; border: 1px solid #81c784;"
                  >
                    <v-chip small color="success" class="mr-3">
                      <v-icon small left>mdi-check</v-icon>มีไฟล์แนบแล้ว
                    </v-chip>
                    <v-btn icon small color="primary" class="mr-2" :href="'/HRvecDecoration/' + editpersonnel_decoration.decoration_file" target="_blank">
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                    <v-btn icon small color="error" @click="editpersonnel_decoration.decoration_file = ''">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <span class="ml-2 text-caption grey--text">ลบไฟล์เดิมเพื่ออัปโหลดใหม่</span>
                  </div>
                  <v-file-input
                    v-else
                    v-model="edit_decoration_file_tem"
                    outlined
                    dense
                    accept=".pdf,.jpg,.jpeg,.png"
                    label="แนบไฟล์ประกาศ/เกียรติบัตร (.pdf/.jpg/.png)"
                    prepend-icon=""
                    prepend-inner-icon="mdi-paperclip"
                    show-size
                    class="modern-input"
                  />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="editpersonnel_decorationdialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn large depressed color="#a86400" dark @click="editpersonnel_decorationSubmit()" class="save-btn" :loading="submitting">
            <v-icon left>mdi-content-save-outline</v-icon>
            บันทึกการแก้ไข
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deletepersonnel_decorationdialog" persistent max-width="450px" content-class="modern-dialog">
      <v-card class="dialog-card delete-dialog">
        <v-card-title class="dialog-header delete-header">
          <v-icon class="mr-3" color="white" size="28">mdi-delete-alert-outline</v-icon>
          <span>ยืนยันการลบข้อมูล</span>
        </v-card-title>

        <v-card-text class="delete-content text-center">
          <div class="delete-icon-wrapper">
            <v-icon size="64" color="#e53935">mdi-alert-circle-outline</v-icon>
          </div>
          <div class="delete-message">คุณต้องการลบรายการนี้หรือไม่?</div>
          <v-chip class="delete-item-chip mt-3" color="#ffebee" text-color="#c62828" large>
            <v-icon left color="#c62828">mdi-medal</v-icon>
            {{ editpersonnel_decoration.decoration_name }} - {{ editpersonnel_decoration.decoration_class }}
          </v-chip>
          <div class="delete-warning mt-4">
            <v-icon small color="#a86400" class="mr-1">mdi-alert</v-icon>
            การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="deletepersonnel_decorationdialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn large depressed color="#e53935" dark @click="deletepersonnel_decorationubmit()" class="delete-confirm-btn">
            <v-icon left>mdi-delete-outline</v-icon>
            ลบข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" :color="snackbar.color" top right class="modern-snackbar">
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { CLASSES, orderName } from "@/utils/decorationClasses";

export default {
  name: "PersonnelDecoration",
  props: {
    editpersonnel_temporary: {
      type: [Object, Array],
      required: false,
      default: () => ({})
    },
    // ใช้ในหน้าต่างของหน้าอื่น (เช่น /college/decoration) — ซ่อนส่วนหัวและสถิติ
    compact: { type: Boolean, default: false }
  },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: true,
      submitting: false,
      search: "",
      addpersonnel_decorationdialog: false,
      editpersonnel_decorationdialog: false,
      deletepersonnel_decorationdialog: false,
      addReceivedDateMenu: false,
      addGazetteDateMenu: false,
      editReceivedDateMenu: false,
      editGazetteDateMenu: false,
      add_decoration_file_tem: null,
      edit_decoration_file_tem: null,
      snackbar: {
        show: false,
        color: "",
        timeout: 4000,
        icon: "",
        text: ""
      },
      headers: [
        { text: "#", align: "center", value: "number_row", width: "60px", sortable: false },
        { text: "ชื่อเครื่องราชอิสริยาภรณ์", align: "left", value: "decoration_name" },
        { text: "ชั้นตรา", align: "left", value: "decoration_class" },
        { text: "วันที่ได้รับพระราชทาน", align: "center", value: "received_date" },
        { text: "ราชกิจจานุเบกษา", align: "left", value: "gazette_book" },
        { text: "ไฟล์แนบ", align: "center", value: "decoration_file", width: "90px" },
        { text: "จัดการ", align: "center", value: "actions", sortable: false, width: "100px" }
      ],
      personnel_decorations: [],
      addpersonnel_decoration: {},
      editpersonnel_decoration: {},
      classItems: CLASSES.slice().reverse()
    };
  },

  computed: {
    latestYear() {
      if (!this.personnel_decorations.length) return null;
      const years = this.personnel_decorations
        .map(d => d.received_date && parseInt(d.received_date.split("-")[0], 10) + 543)
        .filter(y => !!y);
      return years.length ? Math.max(...years) : null;
    },
    distinctClassCount() {
      return new Set(this.personnel_decorations.map(d => d.decoration_class).filter(Boolean)).size;
    },
    withFileCount() {
      return this.personnel_decorations.filter(d => d.decoration_file).length;
    }
  },

  async mounted() {
    await this.personnel_decorationsQueryAll();
  },

  methods: {
    idCardOfPerson() {
      return (
        this.editpersonnel_temporary.id_card ||
        this.editpersonnel_temporary.personnel_etc_id_card ||
        ""
      );
    },

    // เลือกชั้นตราจากรายการ → เติมชื่อเครื่องราชฯ ตามตระกูล (ช้างเผือก/มงกุฎไทย) ให้อัตโนมัติ
    fillOrderName(target, abbr) {
      const name = orderName(abbr);
      if (name) this.$set(target, "decoration_name", name);
    },

    formatDateThai(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      const thaiYear = parseInt(year, 10) + 543;
      return `${day}/${month}/${thaiYear}`;
    },

    showSnackbar(type, text) {
      const configs = {
        success: { color: "#2e7d32", icon: "mdi-check-circle-outline" },
        error: { color: "#e53935", icon: "mdi-alert-circle-outline" }
      };
      this.snackbar = { show: true, timeout: 4000, text, ...configs[type] };
    },

    async uploadDecorationFile(file) {
      const idCard = this.idCardOfPerson();
      const filename = `${idCard}.${Date.now()}.decoration.pdf`;
      const formData = new FormData();
      formData.append("ApiKey", this.ApiKey);
      formData.append("file", file);
      formData.append("filename", `../HRvecDecoration/${filename}`);

      const result = await this.$http.post("uploadfile.php", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (!result.data || result.data.status !== true) {
        throw new Error(
          (result.data && result.data.message) || "อัปโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"
        );
      }
      return filename;
    },

    async personnel_decorationsQueryAll() {
      this.loading = true;
      let result = await this.$http
        .post("personnel_decoration.php", { ApiKey: this.ApiKey, id_card: this.idCardOfPerson() })
        .finally(() => (this.loading = false));
      this.personnel_decorations = result.data;
    },

    personnel_decorationAdd() {
      this.addpersonnel_decoration = {};
      this.add_decoration_file_tem = null;
      this.addpersonnel_decorationdialog = true;
    },

    async addpersonnel_decorationSubmit() {
      if (!this.$refs.addpersonnel_decorationform.validate()) return;
      this.submitting = true;
      try {
        if (this.add_decoration_file_tem) {
          this.addpersonnel_decoration.decoration_file = await this.uploadDecorationFile(
            this.add_decoration_file_tem
          );
        }
        this.addpersonnel_decoration.ApiKey = this.ApiKey;
        this.addpersonnel_decoration.id_card = this.idCardOfPerson();

        let result = await this.$http.post(
          "personnel_decoration.insert.php",
          this.addpersonnel_decoration
        );

        if (result.data.status === true) {
          Swal.fire({
            icon: "success",
            title: "บันทึกสำเร็จ",
            text: "เพิ่มเครื่องราชอิสริยาภรณ์เรียบร้อยแล้ว",
            showConfirmButton: false,
            timer: 1500
          });
          this.personnel_decorationsQueryAll();
          this.$emit("changed");
          this.addpersonnel_decorationdialog = false;
        } else {
          Swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text: "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
            showConfirmButton: true
          });
        }
      } catch (e) {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: e.message, showConfirmButton: true });
      } finally {
        this.submitting = false;
      }
    },

    async personnel_decorationEdit(id_pd) {
      let result = await this.$http.post("personnel_decoration.php", {
        ApiKey: this.ApiKey,
        id_pd: id_pd
      });
      this.editpersonnel_decoration = result.data;
      this.edit_decoration_file_tem = null;
      this.editpersonnel_decorationdialog = true;
    },

    async editpersonnel_decorationSubmit() {
      if (!this.$refs.editpersonnel_decorationform.validate()) return;
      this.submitting = true;
      try {
        if (this.edit_decoration_file_tem) {
          this.editpersonnel_decoration.decoration_file = await this.uploadDecorationFile(
            this.edit_decoration_file_tem
          );
        }
        this.editpersonnel_decoration.ApiKey = this.ApiKey;

        let result = await this.$http.post(
          "personnel_decoration.update.php",
          this.editpersonnel_decoration
        );

        if (result.data.status === true) {
          Swal.fire({
            icon: "success",
            title: "แก้ไขสำเร็จ",
            text: "อัปเดตข้อมูลเรียบร้อยแล้ว",
            showConfirmButton: false,
            timer: 1500
          });
          this.personnel_decorationsQueryAll();
          this.$emit("changed");
          this.editpersonnel_decorationdialog = false;
        } else {
          Swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text: "ไม่สามารถแก้ไขข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
            showConfirmButton: true
          });
        }
      } catch (e) {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: e.message, showConfirmButton: true });
      } finally {
        this.submitting = false;
      }
    },

    async personnel_decorationDelete(id_pd) {
      let result = await this.$http.post("personnel_decoration.php", {
        ApiKey: this.ApiKey,
        id_pd: id_pd
      });
      this.editpersonnel_decoration = result.data;
      this.deletepersonnel_decorationdialog = true;
    },

    async deletepersonnel_decorationubmit() {
      this.editpersonnel_decoration.ApiKey = this.ApiKey;

      let result = await this.$http.post(
        "personnel_decoration.delete.php",
        this.editpersonnel_decoration
      );

      if (result.data.status === true) {
        Swal.fire({
          icon: "success",
          title: "ลบสำเร็จ",
          text: "ลบข้อมูลเรียบร้อยแล้ว",
          showConfirmButton: false,
          timer: 1500
        });
        this.personnel_decorationsQueryAll();
        this.$emit("changed");
      } else {
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
          showConfirmButton: true
        });
      }
      this.deletepersonnel_decorationdialog = false;
    }
  }
};
</script>

<style scoped>
.hero-header {
  background: var(--hv-hero);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}
.hero-content {
  display: flex;
  align-items: center;
  padding: 32px;
  position: relative;
  z-index: 1;
}
.hero-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 24px;
  backdrop-filter: blur(10px);
}
.hero-text {
  color: white;
}
.hero-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}
.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}
.add-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #e53935 !important;
  font-weight: 500;
  border-radius: 12px;
  padding: 0 24px !important;
  text-transform: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}
.stats-row {
  margin-top: -20px;
}
.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid rgba(230, 57, 53, 0.1);
  box-shadow: 0 2px 8px rgba(229, 57, 53, 0.05);
}
.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.stat-card-1 .stat-icon {
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
}
.stat-card-2 .stat-icon {
  background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
}
.stat-card-3 .stat-icon {
  background: linear-gradient(135deg, #c62828 0%, #b3151b 100%);
}
.stat-card-4 .stat-icon {
  background: linear-gradient(135deg, #bf360c 0%, #8d2815 100%);
}
.stat-content {
  display: flex;
  flex-direction: column;
}
.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #263238;
  line-height: 1;
}
.stat-label {
  font-size: 13px;
  color: #718096;
  margin-top: 4px;
}
.main-table-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(229, 57, 53, 0.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.table-header {
  padding: 20px 24px;
  background: #fff5f5;
  border-bottom: 1px solid rgba(229, 57, 53, 0.1);
}
.header-left {
  display: flex;
  align-items: center;
}
.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #263238;
}
.modern-table >>> thead th {
  background: #fff5f5 !important;
  color: #c62828 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  border-bottom: 2px solid #ffcdd2 !important;
}
.table-row:hover {
  background: #fff9f9 !important;
}
.branch-cell {
  display: flex;
  align-items: center;
}
.sub-branch-text {
  color: #718096;
  font-size: 13px;
}
.action-btn {
  width: 32px !important;
  height: 32px !important;
}
.edit-btn {
  background: #fff4dc !important;
  color: #a86400 !important;
}
.delete-btn {
  background: #ffebee !important;
  color: #e53935 !important;
}
.modern-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}
.dialog-card {
  border-radius: 20px !important;
  overflow: hidden;
}
.dialog-header {
  padding: 20px 24px;
  font-size: 18px;
  font-weight: 600;
  color: white;
}
.add-header {
  background: var(--hv-header-navy);
}
.edit-header {
  background: linear-gradient(135deg, #a86400 0%, #a86400 100%);
}
.delete-header {
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
}
.dialog-content {
  padding: 24px !important;
  max-height: 70vh;
  overflow-y: auto;
}
.form-section {
  background: #fff5f5;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #ffcdd2;
}
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #c62828;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}
.modern-input >>> .v-input__slot {
  background: white !important;
  border-radius: 10px !important;
  border: 1px solid #e0e0e0 !important;
}
.dialog-actions {
  padding: 16px 24px;
  background: #fff5f5;
}
.cancel-btn {
  color: #718096 !important;
  font-weight: 500;
  text-transform: none;
}
.save-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
  padding: 0 24px !important;
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%) !important;
}
.delete-content {
  padding: 32px 24px !important;
}
.delete-message {
  font-size: 18px;
  font-weight: 500;
  color: #263238;
}
.delete-warning {
  font-size: 13px;
  color: #e53935;
}
.delete-confirm-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
  background: #e53935 !important;
}
.modern-snackbar >>> .v-snack__wrapper {
  border-radius: 12px;
}
</style>
